import { Card } from 'antd';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PhlebotomistStats } from '../../../types/phlebotomist-dashboard';
import { ExpectedVsActualChart } from '../../charts/ExpectedVsActualChart';
import { PhlebTooltip } from '../../PhlebTooltip';

interface IProductYieldCardProps {
  stats: PhlebotomistStats | null;
  loading?: boolean;
  /** Measured pane width from parent - used for responsive calculations */
  paneWidth?: number;
  /** Whether we're in split screen mode */
  isSplitMode?: boolean;
  /** Comparison stats for inline compare mode */
  comparisonStats?: PhlebotomistStats | null;
  /** Current date range label for legend (inline mode) */
  currentDateRangeLabel?: string;
  /** Comparison date range label for legend (inline mode) */
  comparisonDateRangeLabel?: string;
  /** Previous period stats for trend arrows (only in normal/default mode) */
  previousPeriodStats?: PhlebotomistStats | null;
}

export const ProductYieldCard: FC<IProductYieldCardProps> = ({
  stats,
  loading = false,
  paneWidth,
  isSplitMode = false,
  comparisonStats,
  currentDateRangeLabel,
  comparisonDateRangeLabel,
  previousPeriodStats,
}) => {
  const { t } = useTranslation('dashboard.PhlebotomistDashboard');
  const [expanded, setExpanded] = useState(false);

  // Use compact layout when pane width <= 500px
  // Default to non-compact if paneWidth not provided
  const isCompact = paneWidth !== undefined ? paneWidth <= 500 : false;

  // In split mode, don't limit bar width so they fill available space
  // In normal mode, limit to 500px max to prevent overly wide bars
  const maxBarWidth = isCompact || isSplitMode ? undefined : 500;

  const chartData = useMemo(() => {
    if (!stats) return [];

    const base = [
      {
        category: t('cards.productYield.total', 'Total'),
        expected: stats.expectedTotal,
        actual: stats.actualTotal,
      },
    ];

    if (expanded) {
      base.push(
        {
          category: t('cards.productYield.totalSgl', 'Total SGL'),
          expected: stats.expectedPRBCSingle + stats.expectedFFPSingle,
          actual: stats.actualPRBCSingle + stats.actualFFPSingle,
        },
        {
          category: t('cards.productYield.totalDbl', 'Total DBL'),
          expected: stats.expectedPRBCDouble + stats.expectedFFPDouble,
          actual: stats.actualPRBCDouble + stats.actualFFPDouble,
        }
      );
    }

    base.push({
      category: t('cards.productYield.prbc', 'pRBC'),
      expected: stats.expectedPRBC,
      actual: stats.actualPRBC,
    });

    if (expanded) {
      base.push(
        {
          category: t('cards.productYield.prbcSgl', 'pRBC SGL'),
          expected: stats.expectedPRBCSingle,
          actual: stats.actualPRBCSingle,
        },
        {
          category: t('cards.productYield.prbcDbl', 'pRBC DBL'),
          expected: stats.expectedPRBCDouble,
          actual: stats.actualPRBCDouble,
        }
      );
    }

    base.push({
      category: t('cards.productYield.ffp', 'FFP'),
      expected: stats.expectedFFPSingle + stats.expectedFFPDouble,
      actual: stats.actualFFPSingle + stats.actualFFPDouble,
    });

    if (expanded) {
      base.push(
        {
          category: t('cards.productYield.ffpSgl', 'FFP SGL'),
          expected: stats.expectedFFPSingle,
          actual: stats.actualFFPSingle,
        },
        {
          category: t('cards.productYield.ffpDbl', 'FFP DBL'),
          expected: stats.expectedFFPDouble,
          actual: stats.actualFFPDouble,
        }
      );
    }

    return base;
  }, [stats, t, expanded]);

  const comparisonChartData = useMemo(() => {
    if (!comparisonStats) return undefined;

    const base = [
      {
        category: t('cards.productYield.total', 'Total'),
        expected: comparisonStats.expectedTotal,
        actual: comparisonStats.actualTotal,
      },
    ];

    if (expanded) {
      base.push(
        {
          category: t('cards.productYield.totalSgl', 'Total SGL'),
          expected:
            comparisonStats.expectedPRBCSingle +
            comparisonStats.expectedFFPSingle,
          actual:
            comparisonStats.actualPRBCSingle + comparisonStats.actualFFPSingle,
        },
        {
          category: t('cards.productYield.totalDbl', 'Total DBL'),
          expected:
            comparisonStats.expectedPRBCDouble +
            comparisonStats.expectedFFPDouble,
          actual:
            comparisonStats.actualPRBCDouble + comparisonStats.actualFFPDouble,
        }
      );
    }

    base.push({
      category: t('cards.productYield.prbc', 'pRBC'),
      expected: comparisonStats.expectedPRBC,
      actual: comparisonStats.actualPRBC,
    });

    if (expanded) {
      base.push(
        {
          category: t('cards.productYield.prbcSgl', 'pRBC SGL'),
          expected: comparisonStats.expectedPRBCSingle,
          actual: comparisonStats.actualPRBCSingle,
        },
        {
          category: t('cards.productYield.prbcDbl', 'pRBC DBL'),
          expected: comparisonStats.expectedPRBCDouble,
          actual: comparisonStats.actualPRBCDouble,
        }
      );
    }

    base.push({
      category: t('cards.productYield.ffp', 'FFP'),
      expected:
        comparisonStats.expectedFFPSingle + comparisonStats.expectedFFPDouble,
      actual: comparisonStats.actualFFPSingle + comparisonStats.actualFFPDouble,
    });

    if (expanded) {
      base.push(
        {
          category: t('cards.productYield.ffpSgl', 'FFP SGL'),
          expected: comparisonStats.expectedFFPSingle,
          actual: comparisonStats.actualFFPSingle,
        },
        {
          category: t('cards.productYield.ffpDbl', 'FFP DBL'),
          expected: comparisonStats.expectedFFPDouble,
          actual: comparisonStats.actualFFPDouble,
        }
      );
    }

    return base;
  }, [comparisonStats, t, expanded]);

  const previousPeriodChartData = useMemo(() => {
    if (!previousPeriodStats) return undefined;

    const base = [
      {
        category: t('cards.productYield.total', 'Total'),
        expected: previousPeriodStats.expectedTotal,
        actual: previousPeriodStats.actualTotal,
      },
    ];

    if (expanded) {
      base.push(
        {
          category: t('cards.productYield.totalSgl', 'Total SGL'),
          expected:
            previousPeriodStats.expectedPRBCSingle +
            previousPeriodStats.expectedFFPSingle,
          actual:
            previousPeriodStats.actualPRBCSingle +
            previousPeriodStats.actualFFPSingle,
        },
        {
          category: t('cards.productYield.totalDbl', 'Total DBL'),
          expected:
            previousPeriodStats.expectedPRBCDouble +
            previousPeriodStats.expectedFFPDouble,
          actual:
            previousPeriodStats.actualPRBCDouble +
            previousPeriodStats.actualFFPDouble,
        }
      );
    }

    base.push({
      category: t('cards.productYield.prbc', 'pRBC'),
      expected: previousPeriodStats.expectedPRBC,
      actual: previousPeriodStats.actualPRBC,
    });

    if (expanded) {
      base.push(
        {
          category: t('cards.productYield.prbcSgl', 'pRBC SGL'),
          expected: previousPeriodStats.expectedPRBCSingle,
          actual: previousPeriodStats.actualPRBCSingle,
        },
        {
          category: t('cards.productYield.prbcDbl', 'pRBC DBL'),
          expected: previousPeriodStats.expectedPRBCDouble,
          actual: previousPeriodStats.actualPRBCDouble,
        }
      );
    }

    base.push({
      category: t('cards.productYield.ffp', 'FFP'),
      expected:
        previousPeriodStats.expectedFFPSingle +
        previousPeriodStats.expectedFFPDouble,
      actual:
        previousPeriodStats.actualFFPSingle +
        previousPeriodStats.actualFFPDouble,
    });

    if (expanded) {
      base.push(
        {
          category: t('cards.productYield.ffpSgl', 'FFP SGL'),
          expected: previousPeriodStats.expectedFFPSingle,
          actual: previousPeriodStats.actualFFPSingle,
        },
        {
          category: t('cards.productYield.ffpDbl', 'FFP DBL'),
          expected: previousPeriodStats.expectedFFPDouble,
          actual: previousPeriodStats.actualFFPDouble,
        }
      );
    }

    return base;
  }, [previousPeriodStats, t, expanded]);

  return (
    <Card
      title={
        <PhlebTooltip
          iconColor="#fff"
          text="Compares the number of product units expected from each donation (based on donor type: SGL=1, DBL=2 per product type) against what was actually collected. Broken down by Total, pRBC, and FFP with optional SGL/DBL detail."
        >
          {t('cards.productYield.title', 'Product Expected vs Actual')}
        </PhlebTooltip>
      }
      loading={loading}
      style={{ height: '100%', background: '#0e4d8f', borderRadius: 8 }}
      headStyle={{
        fontSize: 18,
        color: '#fff',
        borderBottom: '1px solid #0b345f',
        marginBottom: '24px',
      }}
      bodyStyle={{
        padding: 5,
        background: '#1565c0',
        margin: '8px 24px 24px',
        borderRadius: 6,
        border: '2px solid #60a5fa',
        boxShadow: '0 0 8px rgba(96, 165, 250, 0.3)',
      }}
      extra={
        <span
          onClick={() => setExpanded(!expanded)}
          style={{
            fontSize: 12,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            backgroundColor: expanded ? '#1e40af' : '#3b82f6',
            color: '#fff',
            padding: expanded ? '4px 10px 2px' : '3px 10px',
            borderRadius: 4,
            cursor: 'pointer',
            userSelect: 'none',
            boxShadow: expanded
              ? 'inset 0 3px 6px rgba(0,0,0,0.35)'
              : '0 2px 4px rgba(0,0,0,0.2), 0 1px 2px rgba(0,0,0,0.15)',
            transform: expanded ? 'translateY(1px)' : 'none',
            transition: 'all 0.15s ease',
          }}
        >
          {expanded
            ? t('cards.productYield.hideSglDbl', 'Hide SGL/DBL')
            : t('cards.productYield.showSglDbl', 'Show SGL/DBL')}
        </span>
      }
    >
      <ExpectedVsActualChart
        data={chartData}
        maxBarWidth={maxBarWidth}
        isCompact={isCompact}
        comparisonData={comparisonChartData}
        currentDateRangeLabel={currentDateRangeLabel}
        comparisonDateRangeLabel={comparisonDateRangeLabel}
        paneWidth={paneWidth}
        pendingDonations={stats?.pendingDonations ?? 0}
        isSplitMode={isSplitMode}
        previousPeriodData={previousPeriodChartData}
      />
    </Card>
  );
};

export default ProductYieldCard;
